import { useMutation } from '@apollo/client';
import type {
  UpdateApprovalMutation,
  UpdateApprovalMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateApprovalDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateApprovalTRPC } from './useUpdateApprovalTRPC';

export const useUpdateApproval = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateApprovalGraphQL, graphqlState] = useMutation(
    UpdateApprovalDocument,
    {
      update: (cache) => {
        evictField(cache, 'approval');
        evictField(cache, 'approval_by_pk');
      },
      refetchQueries: [
        namedOperations.Query.getGlobalApprovals,
        namedOperations.Query.getApprovalLevels,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateApprovalTRPC();

  const updateApproval = async (
    variables: UpdateApprovalMutationVariables
  ): Promise<UpdateApprovalMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateApproval(variables);
    }

    const result = await updateApprovalGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to update approval');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateApproval,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateApproval,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
